import { BaseLanguage } from "./baseLang.service";
import { SemanticErrorCause, SyntaxErrorCause } from "app/xlogo-parser";

export class GermanService extends BaseLanguage {
  //#################################################################################
  //
  // This service holds hashmap containing all german strings used in our platform
  // When translating, please keep keys (first argument) same, while
  // adapting the values (seconds argument).
  //
  //#################################################################################

  constructor() {
    super();

    this.populateStaticStrings();
    this.defineLoginStrings();
    this.defineExamStrings();
    this.defineOnlineStorageStrings();
    this.defineQickStartStrings();
    this.defineRuntimeErrorStrings();
    this.defineSemanticErrorStrings();
    this.defineSyntaxErrorStrings();
    this.defineDebugWatchStrings();
    this.defineDebugControls();
    this.defineTooltips();
    this.defineProgressStrings();
    this.defineRobotStrings();
    this.defineUIstrings();
    this.defineMenuStrings();
  }

  // strings used in menu
  private defineMenuStrings(){
    this.define("menu", "Menü");
    this.define("saveFile", "Datei speichern");
    this.define("loadFile", "Datei laden");
    this.define("saveCanvas", "Bild speichern");
    this.define("fullscreen", "Vollbildmodus");
    this.define("newfeatures", "Was ist neu?");
    this.define("Cheatsheet", "Befehlsübersicht");
    this.define("background", "Hintergrundbilder");
    this.define("save-as-png", "Als png speichern");
    this.define("save-as-svg", "Als svg speichern");
    this.define("Robot", "Roboter");
  }

  // user interface strings: history, header, input and general UI
  private defineUIstrings(){
    this.define("enterCommand", "Befehle hier eingeben");
    this.define("cancel", "abbrechen");
  }

  // Robots interaction strings for BlueBot and Root
  private defineRobotStrings(){
    this.define("WhichRobotUsed", "Welchen Roboter benutzt du?");
    this.define("disconnectBlueBot","BlueBot ist bereits verbunden. Verbindung trennen?");
    this.define("RobotConnected","Root ist bereits verbunden. Verbindung trennen?");
    this.define("ConnectionNotPossible", "Verbindung nicht möglich");
    this.define("ConnectionError","Der Computer/Browser unterstützt keine Bluetooth Low Energy Geräte. Aktualisiere deine Bluetooth Treiber und versuche es erneut mit einem anderen Gerät oder Browser.");
    this.define("RobotDisconnect", "Ja, Verbindung trennen");
  }

  //Progress strings shown in header while execution
  private defineProgressStrings(){
    this.define("executingVisitor", "Berechne Resultat...");
    this.define("init", "Initialisieren...");
  }

  // Strings related to tooltips in header
  private defineTooltips(){
    this.define("tooltipButtonStop", "Laufendes Programm stoppen");
    this.define("tooltipButtonDebug", "Programm Schritt für Schritt ausführen");
    this.define("tooltipButtonForward","Einen Schritt zurück zum letzten Befehl");
    this.define("tooltipButtonBackward", "Nächsten Befehl ausführen");
    this.define("tooltipButtonSpeed", "Geschwindigkeit anpassen");
    this.define("tooltipButtonLanguage", "Sprache ändern");
    this.define("tooltipButtonTurtle", "Schildkröte ändern");
  }

  // Strings related to debug controls in header
  private defineDebugControls(){
    this.define("buttonDebugToggle", "Schrittweise");
    this.define("buttonForward", "Vorwärts");
    this.define("buttonBackward", "Rückwärts");  
  }

  // Strings related to ebug watch
  private defineDebugWatchStrings(){
    this.define("watchPenDown", "Stift unten:");
    this.define("watchErasing", "Radieren:");
    this.define("watchVisible", "Sichtbar:");
    this.define("watchColor", "Stiftfarbe:");
    this.define("watchWidth", "Stiftbreite");
    this.define("watchDirection", "Ausrichtung:"); 
  }

  // Strings related to syntactic errors
  private defineSyntaxErrorStrings(){
    this.define(SyntaxErrorCause[SyntaxErrorCause.EXTRANEOUS_INPUT], "Überflüssiges Zeichen {extraneousToken}.");
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISMATCHED_INPUT],"Ungültiges Zeichen {mismatchedToken}. ");
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISSING_INPUT], "Fehlendes Zeichen {missingToken} vor {missingPosition}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.NO_VIABLE_ALT], "Ungültiger Programmtext bei {no_viable_alt_position}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.UNKNOWN], "Es gab einen unbekannten Fehler: {msg}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.FAILED_PREDICATE], "{msg}"); //this one should not be used. It indicates an unexpected error on our side
  }

  // Strings related to semantic errors
  private defineSemanticErrorStrings(){
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_NAME], "Dieser Programmname wurde schon verwendet");
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_PARAMETER],"Diesen Parameternamen verwendest du doppelt: {parameter}");
    this.define(SemanticErrorCause[SemanticErrorCause.INVALID_PROGRAM_NAME],"Dieser Programname ist nicht erlaubt");
    this.define(SemanticErrorCause[SemanticErrorCause.NOT_ENOUGH_ARGUMENTS],"Zu wenig Parameter für '{program}'. Es wird/werden {number_of_arguments} Parameter erwartet.");
    this.define(SemanticErrorCause[SemanticErrorCause.TOO_MANY_ARGUMENTS],"Zu viele Parameter für '{program}'. Es wird/werden {number_of_arguments} Parameter erwartet.");
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_PROGRAM],"Das Program '{program}' existiert nicht (oder es enthält Fehler).");
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_VARIABLE],"Die Variable oder der Parameter '{variable}' wurde nie definiert."); 
  }

  // Strings related to runtime errors
  private defineRuntimeErrorStrings(){
    this.define("runtime_error_unknown_program","Das Program '{program}' existiert nicht (oder es enthält Fehler)");
    this.define("runtime_error_unknown_function","Die Funktion {function_name} existiert nicht. Dies ist ein Bug. Bitte melden.");
    this.define("runtime_error_unknown_builtin","Der Befehl '{command}' existiert nicht. Dies ist ein Bug. Bitte melden.");
    this.define("runtime_error_unknown_operator","Der Operator '{operator}' existiert nicht. Dies ist ein Bug. Bitte melden.");
    this.define("runtime_error_generic_invalid_argument","Ungültiger Parameter für '{program}': {argument}");
    this.define("runtime_error_division_by_zero","Division durch 0 ist nicht möglich");
    this.define("runtime_error_generic_not_in_range","Der Parameter für {program} muss zwischen {lower_bound} und {upper_bound} liegen");
    this.define("runtime_error_invalid_argument_tan","Der Parameter für tan darf nicht die Form (0.5 + k) * PI haben");
    this.define("runtime_error_mod_by_zero","Es ist nicht möglich mod n 0 zu berechnen");
    this.define("runtime_error_invalid_argument_colornumber","Die angegebene Farbnummer existiert nicht: {color_number}");
    this.define("runtime_error_invalid_argument_colorname","Der angegebene Farbname existiert nicht: {color_name}");
    this.define("runtime_error_invalid_argument_rgb", "Die Werte einer Farbe müssen Zahlen sein: {color}");
    this.define("runtime_error_rgb_out_of_range","Die angegebene Farbe is ungültig: {color}");
    this.define("runtime_error_sqrt_negative_number","Die Wurzel von negativen Zahlen zu berechnen ist nicht möglich");
    this.define("runtime_error_log10_negative_number", "Den Logarithmus von negativen Zahlen zu berechnen ist nicht möglich");
    this.define("runtime_error_setpw_negative_number","Eine negative Stiftbreite zu setzen ist nicht möglich");
    this.define("runtime_error_wait_negative_number","Eine negative Zeit zu warten ist nicht möglich");
    this.define("runtime_error_circle_negative_number","Einen Kreis mit negativem Radius zu zeichnen ist nicht möglich");  
  }

  // Strings related to quickstarter
  private defineQickStartStrings(){
    this.define("tutorialtype", "Wähle ein Tutorial.");
    this.define("doyouneedhelp", "Brauchst du Hilfe?");
    this.define("firststeps", "Erste Schritte");
    this.define("definenewcommands", "Definiere neue Befehle");
    this.define("debugger", "Schritt-für-Schritt Modus"); 
    this.define("intro1","Willkommen in XLogoOnline. In diesem Tutorial lernst du, farbige Muster zu programmieren.");
    this.define("intro2", "Schau, die Schildkröte zeichnet ein Mandala! Was wir sehen, ist das Resultat eines Programms.");
    this.define("intro3", "Programme bestehen aus Befehlen. Zwei solche Befehle sind FD und BK (die wir verwenden, um Linien zeichnen). RT und LT drehen die Schildkröte nach rechts oder links.");
    this.define("intro4", "Probier es aus! Klicke ins Feld und bestätige den Befehl mit ENTER");
    this.define("intro5", "Die Schildkröte führt jeden deiner Befehle aus. Das Resultat siehst du hier auf der Zeichenfläche");
    this.define("intro6", "Lösche die Zeichnung mit dem Befehl CS.");
    this.define("intro7","CS löscht alle Linien und setzt die Schildkröte zurück in die Mitte");
    this.define("intro8", "Progamme können auch aus mehreren Befehlen bestehen. Finde heraus, was hier gezeichnet wird!");
    this.define("intro9", "Alle bereits ausgeführten Programme werden hier der Reihe nach aufgelistet.");
    this.define("intro10", "Mit den PFEIL-TASTEN " + String.fromCharCode(8679) + " und " + String.fromCharCode(8681) + " können frühere Programme (ohne sie nochmals zu schreiben) wieder in die Eingabezeile geladen werden.");
    this.define("intro11", "Was geschieht eigentlich, wenn wir einen Fehler machen?");
    this.define("intro12","Dieses Programm ist fehlerhaft. Siehst du den Fehler? Teste, was die Schildkröte mit falschen Programmen macht.");
    this.define("intro13", "Die Schildkröte hat die Anweisung nicht verstanden. Anstatt einer Zeichnung erhalten wir einen Hinweis auf den Fehler und dessen Ursache (hier in orange).");
    this.define("intro14", "Los geht's. Viel Spass beim Programmieren!");
    this.define("editor1","In diesem Tutorial zeigen wir dir, wie du neue Befehle definierst und verwendest.");
    this.define("editor2","Schau dir dieses komplexe Programm an! Wer erratet, was das Programm tut?");
    this.define("editor3","Was? So ein kompliziertes Programm, bloss um vier Quadrate zu zeichnen?!");
    this.define("editor4","Kann man das nicht viel einfacher machen, zum Beispiel so?");
    this.define("editor5","Leider nicht; den Befehl QUADRAT kennt die Schildkröte nämlich nicht... Lasst uns also einen neuen Befehl definieren!");
    this.define("editor6","Neue Befehle definieren wir hier. Dieses Feld nennen wir Editor.");
    this.define("editor7","'TO' QUADRAT 'END' definiert einen neuen Befehl mit dem Namen QUADRAT." );
    this.define("editor8","In demselben Fenster können auch mehrere Befehle definiert werden.");
    this.define("editor9","Hoppla! Siehst du das rote Kreuz in Zeile 5? Es zeigt an, dass sich auf dieser Zeile ein Fehler versteckt. Was könnte es sein? Derselbe Befehl QUADRAT wurde mehrfach definiert. (Tipp: Nach diesem Tutorial kannst du mit der Maus übers Kreuz fahren und erfährst weitere Details zum Fehler)");
    this.define("editor10", "Was genau soll eigentlich geschehen, wenn der Befehl QUADRAT aufgerufen wird? Wir notieren die entsprechenden Befehle zwischen TO und END");
    this.define("editor11","Lasst uns testen, was der neue Befehl bewirkt. Was denkst du? Probier es aus.");
    this.define("editor12","Ein Quadrat, und das mit nur einem einzigen Befehl! Praktisch! Damit lassen sich nun auch kompliziertere Bilder ganz einfach zeichnen...");
    this.define("editor13","Gratulation! Du hast deinen ersten eigenen Befehl definiert (im Editor) und aufgerufen. Viel Spass beim programmieren!"); 
  }

  // Strings related to storage functionality
  private defineOnlineStorageStrings(){
    this.define("loginButton", "Login");
    this.define("logoutButton", "Logout");
    this.define("registerButton", "Ich bin neu...");
    this.define("loginTitle", "Login");
    this.define("username", "Benutzername");
    this.define("password", "Passwort (optional)");
    this.define("successfulLoginTitle", "Erfolgreich angemeldet");
    this.define("successfulLoginText", "Du bist angemeldet als ");
    this.define("failLoginTitle", "Anmelden fehlgeschlagen");
    this.define("tryAgain", "Versuchs nochmal!");
    this.define("registerTitle", "Registieren");
    this.define("repeatPassword", "Passwort wiederholen");
    this.define("passwordMisatchTitle", "Fehler");
    this.define("passwordMisatchText","Die beiden Passwörter stimmen nicht überein. Möchtest du es nochmals probieren?");
    this.define("successfulRegistrationTitle", "Registrierung erfolgreich");
    this.define("successfulRegistrationText","Gratulation! Du hast dich erfolgreich registiert und bist nun angemeldet als ");
    this.define("serversuccess", "Erfolg");
    this.define("unknownuser", "Unbekannter Benutzername");
    this.define("wrongpassword", "Falsches Passwort");
    this.define("usernamealreadytaken","Dieser Benutzername existiert bereits.");
    this.define("Ihaveread", "Ich habe die ");
    this.define("datenschutz", "Datenschutzerklärung");
    this.define("aswellas", "und die");
    this.define("terms", "Nutzungsbestimmungen");
    this.define("readandaccepted", "gelesen und bin damit einverstanden");
    this.define("toomuchtext", "Das Dokument ist zu gross");
    this.define("toomuchtexthint","Tipp: Du kannst zu jeder Aufgabe eine separate Lösung speichern");
    this.define("documentexists", "Dokument existiert bereits");
    this.define("overwrite", "Möchtest du es überschreiben?");
    this.define("writtensuccessfully", "Dokument erfolgreich gespeichert");
    this.define("doYouWantToLogOut", "Möchtest du dich ausloggen?");
    this.define("didYouSaveYourPrograms","Hast du deine Programme schon gespeichert?");
    this.define("announceTitle", "Aufgaben suchen");
  }

  // Strings related to Exam mode
  private defineExamStrings(){
    this.define("handInTask", "Aufgabe einreichen");
    this.define("choseAnOption", "Wähle eine der Optionen unten");
    this.define("startExamMode", "Knobelaufgaben");
    this.define("waitingForExamToStart", "Bereit. Warte auf Start.");
    this.define("examFinished","Der Wettbewerb ist zu Ende.");
    this.define("checkingForExam", "Aufgaben laden...");
    this.define("cancelExamModeButton", "Abbrechen");
    this.define("enterExamModeButton", "Aufgaben laden...");
    this.define("infoRecognition", "Bitte Zugriffscode eingeben.");
    this.define("notauthorizedTitle", "Bitte einloggen");
    this.define("notauthorizedText","Um am Wettbewerb teilzunehmen, musst du dich einloggen");
  }

  // Strings related to login
  private defineLoginStrings() {
    this.define("notingtosave", "Nichts zu speichern? \n Dein Editor links ist leer...");
    this.define("howtosave", "Wie möchtest du speichern?");
    this.define("save", "Speichern");
    this.define("locally", "Lokal");
    this.define("online", "Online");
    this.define("load", "Programme laden");
    this.define("noconnection", "Verbindung fehlgeschlagen");
    this.define("filename", "Name meiner Datei:");
    this.define("login", "Login: ");
    this.define("chapterx", "Kapitel X Aufgabe Y");
    this.define("loadprogram", "Lade Programmname:");
    this.define("chooseprogram", "Wähle ein Programm");
    this.define("youcanloadandstore", "Dateien online speichern/laden " + "\u2192" + " im Menu");
    this.define("logoutsuccess", "Erfolgreich abgemeldet");
  }

  // These strings are static and do not need to be translated ever!
  populateStaticStrings() {
    this.define("XLogoOnline", "XLogoOnline");
  }
}



// WEBPACK FOOTER //
// ./src/app/languages/german.service.ts